/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=48%3A264
import type { ReactNode } from 'react';
import React, { useCallback, useState } from 'react';
import type { Interpolation } from '@emotion/serialize';
import type { Theme } from '@emotion/react';
import { Box, Divider } from '@mui/material';
import { useHistory } from 'react-router';
import type { GlobalToken } from 'antd';
import type { ITheme } from '../../../component_library/styles/theme';
import Typography, {
  useGetTypographyStyles,
  TypographyVariant,
} from '../../../component_library/components/Typography';
import { DataElementId } from '../../../constants/data-element-tracking-ids';
import { MenuToopltipWithLinks } from '../../../component_library/components/Tooltip/MenuToopltipWithLinks';
import {
  Button,
  ButtonVariant,
} from '../../../component_library/components/Button';

interface IResourcesDropdown {
  title: string | undefined;
  description: string | undefined;
  links: {
    icon: string;
    label: string;
    path: string;
  }[];
  dropdownTitle: string | undefined;
  additionalInfo: ReactNode;
  additionalStyles: Interpolation<Theme>;
  theme: ITheme;
  themeToken: GlobalToken;
}

const ResourcesDropdown: React.FC<IResourcesDropdown> = ({
  dropdownTitle,
  title,
  description,
  links,
  theme,
  themeToken,
  additionalInfo,
  additionalStyles,
}) => {
  const [openTooltip, setOpenTooltip] = useState(false);
  const history = useHistory();

  const linkTypographyStyles = useGetTypographyStyles(
    TypographyVariant.LARGE_BODY_REGULAR,
    theme,
    themeToken,
    theme.colors.secondary,
  );

  const handleOpenPage = useCallback(
    (pageUrl: string, handleCloseTooltipWithNoTransition: () => void) => {
      handleCloseTooltipWithNoTransition();
      history.push(pageUrl);
    },
    [history],
  );

  const tooltipStyles = {
    popper: {
      sx: {
        '& .MuiTooltip-tooltip': {
          maxWidth: '501px',
          backgroundColor: theme.colors.grayscale.white,
          boxShadow: '0px 2px 3px 0px #00000026',
          padding: 0,
          left: '-100px !important',
          top: '30px',
        },
      },
    },
    arrow: {
      sx: {
        color: theme.colors.grayscale.backgroundGrey,
        fontSize: '30px',
        left: '100px !important',
      },
    },
  };

  const renderTooltipContent = ({
    handleCloseTooltipWithNoTransition,
  }: {
    handleCloseTooltipWithNoTransition: () => void;
  }) => (
    <>
      <Box
        sx={{
          backgroundColor: theme.colors.grayscale.backgroundGrey,
          padding: '40px 24px 40px 24px',
          width: '210px',
          boxSizing: 'border-box',
          borderRadius: '4px 0 0 4px',
        }}
      >
        <Typography
          variant={TypographyVariant.H3}
          additionalStyles={{ marginBottom: '20px' }}
        >
          {title}
        </Typography>
        <Typography variant={TypographyVariant.SMALL_BODY_REGULAR}>
          {description}
        </Typography>
      </Box>
      <div>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            padding: '40px 40px 20px',
          }}
        >
          {links?.map((link) => (
            <Button
              key={link.label}
              type="button"
              dataElementId={DataElementId.HEADER_LINK_RESOURCE_GENERIC}
              onClick={() =>
                handleOpenPage(link.path, handleCloseTooltipWithNoTransition)}
              icon={(
                <i
                  className={link.icon}
                  css={{
                    color: theme.colors.grayscale.inactiveGrey1,
                    fontSize: '18px',
                  }}
                />
              )}
              variant={ButtonVariant.FLAT}
              inlineButton
              hideDecorations
              themeToken={themeToken}
              additionalStyles={{
                ...linkTypographyStyles,
                outline: 'none',
                padding: 0,
                marginBottom: '20px',
              }}
            >
              {link.label}
            </Button>
          ))}
        </div>
        <Divider variant="middle" />
        <div css={{ padding: '20px 40px' }}>{additionalInfo}</div>
      </div>
    </>
  );

  return (
    <MenuToopltipWithLinks
      dataElementId={DataElementId.HEADER_RESOURCE_DROPDOWN}
      titleProps={{
        content: (
          <Typography
            variant={TypographyVariant.MEDIUM_BODY_BOLD}
            theme={theme}
            additionalStyles={{
              marginRight: '8px',
              position: 'relative',
            }}
          >
            {dropdownTitle}
          </Typography>
        ),
        additionalStyles,
        additionalIconStyles: { color: theme.colors.secondary },
      }}
      renderTooltipContent={renderTooltipContent}
      openTooltip={openTooltip}
      setOpenTooltip={setOpenTooltip}
      theme={theme}
      componentsProps={tooltipStyles}
      isInHeader
    />
  );
};

export default ResourcesDropdown;
